import { GroupTasksRepository, GroupTaskCreateDto } from '@/tasks/repositories/group-tasks-repository';
import { formatIsoDateTime } from '@/date-time/date-time-utils';
import { enrollmentTeamPlaceholder } from '@/enrollment-center/models/enrollment-center-models';

export interface TaskCreationData {
    taskType: number;
    description: string;
    startDate: string;
    startTime: string;
    assignedTo?: number;
    assignToPosition?: number;
    sendEmailReminderStaff: boolean;
    sendTextReminderStaff: boolean;
    showTimezone: boolean;
    chosenTimezone: string;
    userTimezone: string;
    familyIncludedIds?: Array<number>;
    familyExcludedIds?: Array<number>;
    orgId?: number;
}

export class TaskCreationService {
    private groupTasksRepository: GroupTasksRepository;

    constructor() {
        this.groupTasksRepository = new GroupTasksRepository();
    }

    /**
     * Creates a group task with the provided data
     * @param filterId - The ID of the group to create tasks for
     * @param taskData - The task creation data
     * @returns Promise with the result of task creation
     */
    async createGroupTask(filterId: number, taskData: TaskCreationData) {
        const timeZone = taskData.showTimezone ? taskData.chosenTimezone : taskData.userTimezone;
        const dueDateFormatted = formatIsoDateTime(taskData.startDate + ' ' + taskData.startTime, timeZone);

        const groupTaskCreateDto: GroupTaskCreateDto = {
            task_activity: taskData.taskType,
            description: taskData.description ? taskData.description : '',
            due_date_time: dueDateFormatted,
            timezone: timeZone,
            notify_staff_email: taskData.sendEmailReminderStaff,
            notify_staff_text: taskData.sendTextReminderStaff,
            show_on_calendar: false, // Not supported in bulk mode
            excluded_families: taskData.familyExcludedIds || null,
            included_families: taskData.familyIncludedIds || null,
            org_id: taskData.orgId
        };

        if (taskData.assignToPosition !== undefined && taskData.assignToPosition !== null) {
            groupTaskCreateDto.assign_to_position = taskData.assignToPosition;
        } else if (taskData.assignedTo !== undefined && taskData.assignedTo !== null) {
            groupTaskCreateDto.assigned_to = this.normalizeAssignedTo(taskData.assignedTo);
        }

        return await this.groupTasksRepository.createGroupTask(filterId, groupTaskCreateDto);
    }

    /**
     * Normalizes the assigned_to value, converting enrollment team placeholder to 0
     * @param assignedTo - The original assigned_to value
     * @returns The normalized assigned_to value
     */
    private normalizeAssignedTo(assignedTo: number): number {
        return assignedTo === enrollmentTeamPlaceholder ? 0 : assignedTo;
    }

    /**
     * Formats date and time with timezone for task creation
     * @param date - The date string
     * @param time - The time string
     * @param showTimezone - Whether to use custom timezone
     * @param chosenTimezone - The chosen timezone
     * @param userTimezone - The user's default timezone
     * @returns Formatted ISO date time string
     */
    formatTaskDateTime(
        date: string,
        time: string,
        showTimezone: boolean,
        chosenTimezone: string,
        userTimezone: string
    ): string {
        const timeZone = showTimezone ? chosenTimezone : userTimezone;
        return formatIsoDateTime(date + ' ' + time, timeZone);
    }
}
